import { zodResolver } from '@hookform/resolvers/zod';
import { FORM_MODE, type ValidationErrorResponse } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Row,
  useConfirmation,
} from '@wyz/ui';
import { useModalState, useOnSuccessQuery } from '@wyz/utils';
import { intersection, isNil, uniq } from 'lodash-es';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router';
import {
  useManageContainerSuppliers,
  useManageCustomers,
  useManageUsers,
} from '~/common/api';
import {
  PrivateComponent,
  SelectField,
  TextField,
  TypeaheadField,
} from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { FEATURES, GROUPS, ROLES } from '~/common/helpers';
import { useAccessControl, useValidationForm } from '~/common/hooks';
import { CommonErrorCodesEnumType } from '~/common/types';
import { GroupSection, ResetPasswordModal } from '~/connect/components';
import { useUserFormPrivileges } from '~/connect/hooks';
import { getUserFormSchema } from '~/connect/schemas';
import { GroupResponse, IUser, IUserForm, IUserPayload } from '~/connect/types';
type DefaultValues = Omit<IUserForm, 'entities' | 'roles'> & {
  entities: IUser['entities'];
};
export type UserFormProps = {
  mode: 'CREATE' | 'UPDATE' | 'DETAIL';
  onSubmit: (formData: IUserPayload) => void;
  onResetPassword?: (formData: IUserPayload) => void;
  isLoading: boolean;
  defaultValues?: DefaultValues;
  errors: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
  wyzAccount?: boolean;
  isLocked?: boolean;
  onUnlock?: () => void;
  unlockLoading?: boolean;
  groups: GroupResponse;
  children?: React.ReactNode;
};

const DEFAULT_VALUES: DefaultValues = {
  firstName: '',
  lastName: '',
  internalCode: '',
  email: '',
  locale: '',
  entities: [],
  groups: [],
  supplierHqId: null,
};

export const UserForm = ({
  errors,
  defaultValues = DEFAULT_VALUES,
  isLoading,
  onSubmit,
  onResetPassword,
  wyzAccount = false,
  isLocked,
  onUnlock,
  unlockLoading,
  groups,
  mode,
  children,
}: UserFormProps) => {
  const { t } = useI18n();
  const acl = useAccessControl();
  const { languages } = useLocale();
  const ask = useConfirmation();
  const manageCustomer = useManageCustomers({
    autocomplete: { enabled: true },
  });
  const [
    resetPasswordModal,
    resetPasswordModalHandlers,
    resetPasswordModalMeta,
  ] = useModalState<'RESET_PASSWORD_MODAL', IUserPayload>({
    defaultValue: undefined,
  });

  const manageUsers = useManageUsers({
    getGroups: { enabled: true },
  });

  const manageContainerSuppliers = useManageContainerSuppliers({
    listHQs: { enabled: acl.isEnabledFeatures([FEATURES.CONTAINER]) },
  });

  const methods = useForm<IUserForm>({
    resolver: zodResolver(getUserFormSchema(wyzAccount, groups)),
    defaultValues: {
      ...defaultValues,
      entities:
        defaultValues?.entities?.map((entity) => {
          return {
            value: entity.id,
            label: `${entity.name} (${entity.code})`,
            level: entity.level,
          };
        }) || [],
    },
  });
  const selectedGroups = useWatch({
    control: methods.control,
    name: 'groups',
  });

  const FormPrivileges = useUserFormPrivileges({
    values: {
      groups: selectedGroups,
    },
    wyzAccount,
    mode,
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const [allowedLevels, setAllowedLevels] = useState<{
    byGroups: Array<number> | undefined;
    byCustomers: Array<number> | undefined;
  }>({ byCustomers: undefined, byGroups: undefined });
  const [fullScopeGroups, setFullScopeGroups] = useState<Array<string>>([]);
  const watchedGroups = useWatch({
    name: 'groups',
    control: methods.control,
  });

  useEffect(() => {
    if (!FormPrivileges.fields.entities.visible) {
      methods.setValue('entities', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups]);

  useOnSuccessQuery(manageUsers.getGroups, (data) => {
    setAllowedLevels({
      byCustomers:
        defaultValues.entities.length > 0
          ? uniq(defaultValues.entities.map((item) => item.level))
          : undefined,
      byGroups:
        defaultValues.groups.length > 0
          ? intersection(
              ...data.data.authorizedGroups
                .filter((group) =>
                  defaultValues.groups.includes(group.codename),
                )
                .map((group) => group.allowedLevels),
            )
          : undefined,
    });
    setFullScopeGroups(
      data.data.authorizedGroups
        .filter((group) => group.fullCustomerScope)
        .map((group) => group.codename),
    );
  });

  return (
    <>
      <FormContainer
        methods={methods}
        submitHandler={(data) => {
          onSubmit({
            ...data,
            entities: data.entities.map((item) => {
              return { id: item.value };
            }),
            supplierHqId: data.supplierHqId ?? null,
          });
        }}
        formConfiguration={{
          mode: mode === 'DETAIL' ? FORM_MODE.READ_ONLY : FORM_MODE.WRITE,
        }}
      >
        <Card className='mb-section'>
          <Card.Body>
            <Row>
              <Col md={5}>
                <TextField
                  name='firstName'
                  label={<Trans i18nKey='app.user.create.form.first_name' />}
                />
              </Col>
              <Col md={5}>
                <TextField
                  name='lastName'
                  label={<Trans i18nKey='app.user.create.form.last_name' />}
                />
              </Col>
              <Col md={2}>
                <TextField
                  name='internalCode'
                  label={<Trans i18nKey='app.user.create.form.internal_code' />}
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <TextField
                  name='email'
                  label={<Trans i18nKey='app.common.email' />}
                  required={mode !== 'DETAIL'}
                  readOnly={!FormPrivileges.fields.email.writable}
                />
              </Col>

              {FormPrivileges.fields.locale.visible && (
                <Col md={5}>
                  <SelectField
                    name='locale'
                    label={<Trans i18nKey='app.common.language' />}
                    options={
                      languages.byIndex?.map((language) => {
                        return {
                          ...language,
                          label: language.humanName,
                          value: language.isoCode,
                        };
                      }) ?? []
                    }
                    required
                    all={{ enabled: false }}
                    maxShown={Infinity}
                  />
                </Col>
              )}
            </Row>
            <Row>
              {FormPrivileges.fields.entities.visible && (
                <Col md={12}>
                  <TypeaheadField
                    isMulti
                    minCharacter={3}
                    onSelect={(data) => {
                      setAllowedLevels((prev) => ({
                        ...prev,
                        byCustomers:
                          data.length === 0
                            ? undefined
                            : uniq(data.map((item) => item.level)),
                      }));
                    }}
                    minCharacterMessage={
                      <Trans
                        i18nKey='app.common.autocomplete.min_character_message'
                        values={{
                          minCharacter: 3,
                        }}
                      />
                    }
                    name='entities'
                    label={<Trans i18nKey='app.user.create.form.entities' />}
                    fetcher={(query, options) =>
                      manageCustomer.autocomplete(query, {
                        ...options,
                        authorizedLevels: allowedLevels.byGroups,
                        limit: 20,
                      })
                    }
                    defaultOptionsFetcher={({ signal }) =>
                      manageCustomer.autocomplete('', {
                        signal,
                        authorizedLevels: allowedLevels.byGroups,
                        limit: 20,
                      })
                    }
                    additionalQueryKeys={allowedLevels.byGroups?.map(String)}
                    defaultValue={defaultValues?.entities.map((item) => ({
                      value: item.id,
                      label: `${item.name} (${item.code})`,
                      level: item.level,
                    }))}
                    wrapItems
                    required={
                      selectedGroups.some((group) =>
                        fullScopeGroups.includes(group),
                      ) &&
                      !watchedGroups.every(
                        (group) => group === GROUPS.GROUP_CONTAINER_SUPPLIER,
                      )
                    }
                    isDisabled={selectedGroups.some((group) =>
                      fullScopeGroups.includes(group),
                    )}
                    maxShown={Infinity}
                  />
                </Col>
              )}
              {watchedGroups.includes(GROUPS.GROUP_CONTAINER_SUPPLIER) && (
                <Col md={12}>
                  <SelectField
                    name='supplierHqId'
                    label={<Trans i18nKey='app.user.create.form.supplier_hq' />}
                    options={
                      manageContainerSuppliers.listHQs.data?.data?.map(
                        (hq) => ({
                          value: hq.id,
                          label: hq.name,
                        }),
                      ) ?? []
                    }
                    required
                    all={{ enabled: false }}
                    maxShown={Infinity}
                  />
                </Col>
              )}
            </Row>
            {children}
          </Card.Body>
        </Card>
        {FormPrivileges.fields.groups.visible && (
          <GroupSection
            mode={mode}
            groups={(
              manageUsers.getGroups.data?.data.authorizedGroups ?? []
            ).filter((group) => {
              return isNil(allowedLevels.byCustomers)
                ? !group.wyz
                : !group.wyz &&
                    group.allowedLevels.some((level) =>
                      allowedLevels.byCustomers?.includes(level),
                    );
            })}
            onSelect={(data) => {
              setAllowedLevels((prev) => ({
                ...prev,
                byGroups:
                  data.length === 0
                    ? undefined
                    : intersection(...data.map((item) => item.allowedLevels)),
              }));
            }}
          />
        )}
        {mode !== 'DETAIL' && (
          <Card>
            <Card.Body>
              <Flex className='space-x-4' justifyContent='between'>
                <Button
                  id='cancel'
                  as={Link}
                  variant='secondary'
                  type='button'
                  to='/users'
                >
                  <Trans i18nKey='app.common.button.cancel' />
                </Button>
                <Flex direction='row' className='space-x-2'>
                  {isLocked && (
                    <PrivateComponent roles={[ROLES.ROLE_USER_UNLOCK]}>
                      <Button
                        id='unlock'
                        variant='secondary'
                        type='button'
                        isLoading={unlockLoading}
                        onClick={() =>
                          ask({
                            title: t(
                              'app.user.detail.unlock.confirmation_message',
                            ),
                            confirmBtnText: t('app.common.button.confirm'),
                            cancelBtnText: t('app.common.button.cancel'),
                          }).onConfirm(() => onUnlock && onUnlock())
                        }
                      >
                        <Trans i18nKey='app.user.detail.unlock.button' />
                      </Button>
                    </PrivateComponent>
                  )}
                  {FormPrivileges.actions.RESET_PASSWORD.visible && (
                    <Button
                      id='reset-button'
                      variant='primary'
                      disabled={isLoading}
                      type='button'
                      onClick={() => {
                        const currentValues = methods.getValues();
                        resetPasswordModalHandlers.open(
                          'RESET_PASSWORD_MODAL',
                          {
                            ...currentValues,
                            entities: currentValues.entities.map((item) => {
                              return { id: item.value };
                            }),
                            supplierHqId: currentValues.supplierHqId ?? null,
                          },
                        );
                      }}
                    >
                      <Trans i18nKey='app.user.edit.reset_password.button' />
                    </Button>
                  )}

                  <Button
                    id='submit'
                    variant='primary'
                    type='submit'
                    disabled={isLoading}
                  >
                    <Trans i18nKey='app.common.button.save' />
                  </Button>
                </Flex>
              </Flex>
            </Card.Body>
          </Card>
        )}
        {resetPasswordModal && resetPasswordModalMeta && onResetPassword && (
          <ResetPasswordModal
            onConfirm={() => onResetPassword(resetPasswordModalMeta)}
            onClose={() => resetPasswordModalHandlers.close()}
            isLocked={isLocked}
          />
        )}
      </FormContainer>
    </>
  );
};
